import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateLoadNatureMutation, useUpdateLoadNatureMutation } from '../../../../hooks/mutations/useLoadNatureMutation';
import { useWasteCodesQuery } from '../../../../hooks/queries/useWasteCodesQuery';
import { LoadNature } from '../../../../services/apiSLTP/materials/loadNatures';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    loadNature: LoadNature | null;
    loadNatures: LoadNature[];
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    showEditModal: boolean;
    refetch: () => void;
}

const UpdateLoadNatureModal: React.FC<ModalProps> = ({ loadNature, isEditMode = true, showEditModal, setShowEditModal, loadNatures, refetch }) => {
    const { t } = useTranslation();
    const [designation, setDesignation] = useState(loadNature?.designation || '');
    const [status, setStatus] = useState(loadNature?.crudStatus || crudStatusEnum.OK);
    const [wasteCode, setWasteCode] = useState<OptionType | null>(
        loadNature?.defaultWasteCodeId ? { label: loadNature.defaultWasteCodeId.designation, value: loadNature.defaultWasteCodeId._id } : null
    );
    const [displayOrder, setDisplayOrder] = useState(typeof loadNature?.displayOrder === 'number' ? loadNature?.displayOrder.toString() : '');

    const { mutate: mutateLoadNature } = useUpdateLoadNatureMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating loadNature:', error.message),
    });

    const { mutate: addLoadNature } = useCreateLoadNatureMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating loadNature:', error.message),
    });

    const { data: dataWasteCodes, isLoading: isWastCodesLoading } = useWasteCodesQuery({ queries: { active: true } });

    const handleActiveChange = (isActive: boolean) => {
        setStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isDuplicateDesignation = () => {
        return loadNatures.some((ln) => ln._id !== loadNature?._id && ln.designation === designation);
    };

    return (
        <ModalBody
            title={t('loadNature')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!designation.trim() || isDuplicateDesignation() || !wasteCode}
            onSubmit={() => {
                if (isEditMode) {
                    if (!loadNature) return;
                    mutateLoadNature({
                        loadNatureId: loadNature?._id,
                        data: {
                            designation,
                            crudStatus: status,
                            displayOrder: displayOrder ? parseInt(displayOrder) : null,
                            defaultWasteCodeId: wasteCode ? wasteCode.value : null,
                        },
                    });
                } else {
                    addLoadNature({
                        designation,
                        crudStatus: status,
                        displayOrder: displayOrder ? parseInt(displayOrder) : undefined,
                        defaultWasteCodeId: wasteCode ? wasteCode.value : undefined,
                    });
                }
            }}
        >
            <CustomTextInput
                value={designation}
                label={t('name')}
                onChange={setDesignation}
                errorText={isDuplicateDesignation() ? t('error_duplicate') : ''}
                required
            />
            <ToggleLine checked={status === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />

            <DropDown
                items={
                    dataWasteCodes?.map((wasteCode) => ({ label: `${wasteCode.designation} - ${wasteCode.description}`, value: wasteCode._id })) || []
                }
                selectedItem={wasteCode}
                handleChange={(event, value) => setWasteCode(value)}
                inputLabel={t('wasteCode')}
                required
            />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateLoadNatureModal;
